/**
 * CMAS Freediving Competition Rules 2025
 * Based on official CMAS documents:
 * - Pool Competitions (Version 2025/01)
 * - Depth Competitions (Version 2025/01 v4)
 */

export const CMAS_RULES = {
  version: "2025/01",
  organization: "CMAS",
  
  // ============================================
  // DISCIPLINES
  // ============================================
  disciplines: {
    // Pool Disciplines
    pool: {
      STA: {
        name: "Static Apnea",
        code: "STA",
        type: "pool",
        description: "Athletes hold their breath on the water's surface for the maximum possible duration",
        measurementUnit: "time", // seconds
        equipment: {
          fins: false,
          mask: "optional",
          noseClip: "optional",
          wetsuit: "optional"
        }
      },
      DYN: {
        name: "Dynamic Apnea (Monofin/Bifins)",
        code: "DYN",
        type: "pool",
        description: "Athletes aim to cover the maximum horizontal distance underwater using monofin or bi-fins",
        measurementUnit: "distance", // meters
        equipment: {
          fins: "monofin_or_bifins",
          mask: "optional",
          noseClip: "optional",
          wetsuit: "optional"
        }
      },
      DYNBF: {
        name: "Dynamic Apnea Bi-Fins",
        code: "DYNBF",
        type: "pool",
        description: "Athletes aim to cover the maximum horizontal distance underwater using bi-fins only",
        measurementUnit: "distance", // meters
        equipment: {
          fins: "bifins_only",
          mask: "optional",
          noseClip: "optional",
          wetsuit: "optional"
        }
      },
      DNF: {
        name: "Dynamic No Fins",
        code: "DNF",
        type: "pool",
        description: "Athletes swim without fins to cover the maximum horizontal distance",
        measurementUnit: "distance", // meters
        equipment: {
          fins: false,
          mask: "optional",
          noseClip: "optional",
          wetsuit: "optional"
        }
      },
      SPEED_2x50: {
        name: "Speed Apnea 2x50m",
        code: "SPEED_2x50",
        type: "pool",
        description: "Time to complete 2x50m distance",
        measurementUnit: "time",
        distance: 100,
        timeLimit: 120, // 2 minutes
        equipment: {
          fins: "monofin_or_bifins",
          mask: "optional"
        }
      },
      SPEED_4x50: {
        name: "Speed Apnea 4x50m",
        code: "SPEED_4x50",
        type: "pool",
        description: "Time to complete 4x50m distance",
        measurementUnit: "time",
        distance: 200,
        timeLimit: 300, // 5 minutes
        equipment: {
          fins: "monofin_or_bifins",
          mask: "optional"
        }
      },
      SPEED_8x50: {
        name: "Speed Apnea 8x50m",
        code: "SPEED_8x50",
        type: "pool",
        description: "Time to complete 8x50m distance",
        measurementUnit: "time",
        distance: 400,
        timeLimit: 600, // 10 minutes
        equipment: {
          fins: "monofin_or_bifins",
          mask: "optional"
        }
      }
    },
    
    // Depth Disciplines
    depth: {
      CWT: {
        name: "Constant Weight",
        code: "CWT",
        type: "depth",
        description: "Athlete dives to declared depth using monofin or bi-fins without changing weight. Cannot pull on the performance line.",
        measurementUnit: "depth", // meters
        canPullRope: false,
        equipment: {
          fins: "monofin_or_bifins",
          mask: "required",
          noseClip: "optional",
          wetsuit: "required"
        }
      },
      CWTB: {
        name: "Constant Weight Bi-Fins",
        code: "CWTB",
        type: "depth",
        description: "Athlete dives to declared depth using bi-fins only without changing weight. Cannot pull on the performance line.",
        measurementUnit: "depth", // meters
        canPullRope: false,
        equipment: {
          fins: "bifins_only",
          mask: "required",
          noseClip: "optional",
          wetsuit: "required"
        }
      },
      CNF: {
        name: "Constant Weight No Fins",
        code: "CNF",
        type: "depth",
        description: "Athlete dives to declared depth without fins and without changing weight. Cannot pull on the performance line.",
        measurementUnit: "depth", // meters
        canPullRope: false,
        equipment: {
          fins: false,
          mask: "required",
          noseClip: "optional",
          wetsuit: "required"
        }
      },
      FIM: {
        name: "Free Immersion",
        code: "FIM",
        type: "depth",
        description: "Athlete dives to declared depth using arms to pull along the rope during descent and ascent.",
        measurementUnit: "depth", // meters
        canPullRope: true,
        equipment: {
          fins: false,
          mask: "required",
          noseClip: "optional",
          wetsuit: "required"
        }
      },
      VWT: {
        name: "Variable Weight",
        code: "VWT",
        type: "depth",
        description: "Athlete descends with a weighted sled and ascends under own power.",
        measurementUnit: "depth", // meters
        canPullRope: true,
        equipment: {
          sled: "required",
          mask: "required",
          wetsuit: "required"
        }
      }
    }
  },

  // ============================================
  // TIMING RULES
  // ============================================
  timing: {
    countdown: {
      // Standard countdown sequence
      sequence: [
        { time: -180, announcement: "3 minutes" },
        { time: -120, announcement: "2 minutes" },
        { time: -90, announcement: "1 minute 30 seconds" },
        { time: -60, announcement: "1 minute" },
        { time: -30, announcement: "30 seconds" },
        { time: -20, announcement: "20 seconds" },
        { time: -10, announcement: "10 seconds" },
        { time: -5, announcement: "5" },
        { time: -4, announcement: "4" },
        { time: -3, announcement: "3" },
        { time: -2, announcement: "2" },
        { time: -1, announcement: "1" },
        { time: 0, announcement: "TOP TIME" },
        { time: 10, announcement: "+10 seconds" },
        { time: 20, announcement: "+20 seconds" },
        { time: 25, announcement: "+25" },
        { time: 26, announcement: "+26" },
        { time: 27, announcement: "+27" },
        { time: 28, announcement: "+28" },
        { time: 29, announcement: "+29" },
        { time: 30, announcement: "+30" }
      ],
      // Speed apnea uses reduced countdown
      speedApneaSequence: [
        { time: -60, announcement: "1 minute" },
        { time: -30, announcement: "30 seconds" },
        { time: -20, announcement: "20 seconds" },
        { time: -10, announcement: "10 seconds" },
        { time: -5, announcement: "5" },
        { time: -4, announcement: "4" },
        { time: -3, announcement: "3" },
        { time: -2, announcement: "2" },
        { time: -1, announcement: "1" },
        { time: 0, announcement: "HORN SIGNAL" }
      ]
    },
    startWindow: {
      start: 0,        // TOP TIME
      end: 30,         // +30 seconds
      earlyStartDQ: true,
      lateStartDQ: true
    },
    interStartDelay: 480, // 8 minutes (480 seconds) minimum between starts
    surfaceProtocol: {
      timeLimit: 20,   // 20 seconds to complete surface protocol
      requirements: [
        "Airways above water surface",
        "Show OK sign (two fingers making circle)",
        "Face the judge",
        "Stay afloat until card is given",
        "No touching lane line or pool edge for support (except Dynamic at turns)",
        "No assistance before card is given"
      ]
    },
    decisionTime: 180  // 3 minutes for judge to give card after attempt
  },

  // ============================================
  // CARD SYSTEM
  // ============================================
  cards: {
    WHITE: {
      code: "WHITE",
      meaning: "Performance valid - in accordance with competition rules",
      validatesRecord: true,
      color: "#FFFFFF"
    },
    YELLOW: {
      code: "YELLOW",
      meaning: "Performance valid with penalty OR decision postponed",
      validatesRecord: false,
      color: "#FFD700",
      reasons: [
        "Penalty applied",
        "Decision postponed for review"
      ]
    },
    RED: {
      code: "RED",
      meaning: "Performance not validated (DQ)",
      validatesRecord: false,
      color: "#FF0000"
    }
  },

  // ============================================
  // PENALTIES
  // ============================================
  penalties: {
    // Pool discipline penalties
    pool: {
      DOLPHIN_KICK_VIOLATION: {
        code: "DOLPHIN_KICK",
        description: "Dolphin kick outside 3-meter zone in bi-fins events",
        penalty: -1, // meters per complete cycle
        unit: "meters",
        perOccurrence: true
      },
      WALL_TOUCH_MISSING: {
        code: "WALL_TOUCH",
        description: "Not touching wall at start in Dynamic",
        penalty: -3,
        unit: "meters"
      },
      SURFACE_PROTOCOL_FAIL: {
        code: "DQ_SP",
        description: "Failed to complete surface protocol within 20 seconds",
        penalty: "DQ",
        unit: "disqualification"
      },
      SURFACE_BLACKOUT: {
        code: "DQ_SURFACE_BO",
        description: "Loss of consciousness at surface",
        penalty: "DQ",
        unit: "disqualification",
        additionalRestriction: "Medical examination required"
      },
      UNDERWATER_BLACKOUT: {
        code: "DQ_UNDERWATER_BO",
        description: "Loss of consciousness underwater",
        penalty: "DQ",
        unit: "disqualification",
        additionalRestriction: "Medical examination required"
      },
      EARLY_START: {
        code: "DQ_EARLY_START",
        description: "Starting before TOP TIME",
        penalty: "DQ",
        unit: "disqualification"
      },
      LATE_START: {
        code: "DQ_LATE_START",
        description: "Airways not immersed at +30 seconds",
        penalty: "DQ",
        unit: "disqualification"
      },
      OUTSIDE_LANE: {
        code: "DQ_SP_OUT",
        description: "Surfaced outside competition lane",
        penalty: "DQ",
        unit: "disqualification"
      },
      INTERFERENCE: {
        code: "DQ_INTERFERENCE",
        description: "Interfering with or obstructing another athlete",
        penalty: "DQ",
        unit: "disqualification"
      },
      OXYGEN_USE: {
        code: "DQ_OXYGEN",
        description: "Use of oxygen enriched air or pure oxygen within 1 hour of attempt",
        penalty: "DQ",
        unit: "disqualification",
        additionalRestriction: "Suspension from competition"
      }
    },
    
    // Depth discipline penalties
    depth: {
      DEPTH_MARKER_MISSING: {
        code: "TAG_MISSING",
        description: "Failure to recover depth marker (tag) or loss of marker",
        penalty: -1,
        unit: "meters"
      },
      DISTANCE_PENALTY: {
        code: "DISTANCE_PENALTY",
        description: "Depth reached less than depth declared",
        calculation: "declared_depth - reached_depth",
        unit: "meters",
        perMeter: true
      },
      PULLING_ROPE_VIOLATION: {
        code: "ROPE_PULL",
        description: "Pulling on rope in CWT/CWTB/CNF disciplines",
        penalty: "DQ",
        unit: "disqualification"
      },
      DEEP_BLACKOUT: {
        code: "DQ_DEEP_BO",
        description: "Loss of consciousness deeper than 15 meters",
        penalty: "DQ",
        unit: "disqualification",
        additionalRestriction: "Cannot dive again in competition"
      }
    },
    
    // Common penalties
    common: {
      DNS: {
        code: "DNS",
        description: "Did Not Start (no show at World Championships)",
        penalty: 300,
        unit: "EUR",
        appliesTo: "World Championships only"
      },
      ASSISTANT_VIOLATION: {
        code: "ASSISTANT",
        description: "Assistant stays in competition area after warning",
        penalty: "general",
        unit: "applied to athlete"
      }
    }
  },

  // ============================================
  // PERFORMANCE CALCULATIONS
  // ============================================
  calculations: {
    // Pool - Dynamic disciplines (DYN, DYNBF, DNF)
    dynamic: {
      formula: "Final Distance = Measured Distance - Penalties",
      rounding: "lower_half_meter", // Round to lower 0.5m
      measurement: "horizontal distance where airways emerge",
      example: {
        measuredDistance: 152.7,
        penalties: 0,
        roundedDistance: 152.5,
        finalResult: 152.5
      }
    },
    
    // Pool - Static (STA)
    static: {
      formula: "Official Time = Mean of Timekeeper Times (rounded down to nearest second)",
      rounding: "down_to_second",
      measurement: "Time from airways immerse after TOP TIME to airways emerge",
      example: {
        timekeeper1: 245.3,
        timekeeper2: 245.7,
        timekeeper3: 245.5,
        mean: 245.5,
        officialTime: 245 // rounded down
      }
    },
    
    // Pool - Speed Apnea
    speedApnea: {
      formula: "Final Time = Time to complete distance",
      rounding: "none", // Exact time
      measurement: "Time from start signal to touching finish wall",
      timeLimits: {
        "2x50": 120,  // 2 minutes
        "4x50": 300,  // 5 minutes  
        "8x50": 600   // 10 minutes
      },
      lateFinishResult: "Classified at end with 'Late Finish' comment"
    },
    
    // Depth disciplines
    depth: {
      formula: "Final Performance = Depth Reached - Distance Penalty - General Penalty",
      distancePenalty: "Declared Depth - Depth Reached (if DR < DD)",
      measurement: "Official depth gauge",
      example: {
        declaredDepth: 100,
        depthReached: 98,
        tagRecovered: true,
        distancePenalty: 2, // 100 - 98
        generalPenalty: 0,
        finalResult: 96 // 98 - 2 - 0
      }
    }
  },

  // ============================================
  // POOL SPECIFICATIONS
  // ============================================
  poolRequirements: {
    worldContinentalRecords: {
      length: 50, // meters
      minDepth: 1.20
    },
    nationalLocalRecords: {
      length: [25, 50], // 25m or 50m
      minDepth: 1.20
    }
  },

  // ============================================
  // ATHLETE CATEGORIES
  // ============================================
  categories: {
    junior: {
      minAge: 16,
      maxAge: 17
    },
    senior: {
      minAge: 18,
      maxAge: null
    },
    masters: {
      brackets: [
        { code: "M35", minAge: 35, maxAge: 39 },
        { code: "M40", minAge: 40, maxAge: 44 },
        { code: "M45", minAge: 45, maxAge: 49 },
        { code: "M50", minAge: 50, maxAge: 54 },
        { code: "M55", minAge: 55, maxAge: 59 },
        { code: "M60", minAge: 60, maxAge: 64 },
        { code: "M65", minAge: 65, maxAge: 69 },
        { code: "M70", minAge: 70, maxAge: 74 },
        { code: "M75", minAge: 75, maxAge: 79 },
        { code: "M80", minAge: 80, maxAge: null }
      ]
    },
    gender: ["male", "female"]
  },

  // ============================================
  // RECORD REQUIREMENTS
  // ============================================
  recordRequirements: {
    validation: "White card required",
    poolLength: "50m for World/Continental records",
    officialPresence: "CMAS Technical Delegate or Chief Judge required",
    competitionCategories: [
      "World Championships",
      "Continental Championships",
      "Zone Championships",
      "World Games",
      "IOC competitions",
      "FISU competitions",
      "CISM",
      "World/Zone/Continental Cups",
      "Masters Championships"
    ]
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Calculate final result for Dynamic disciplines
 * @param {number} measuredDistance - Distance in meters
 * @param {number} penalties - Total penalties in meters
 * @returns {number} Final distance rounded to lower 0.5m
 */
export const calculateDynamicResult = (measuredDistance, penalties = 0) => {
  const netDistance = measuredDistance - penalties;
  return Math.floor(netDistance * 2) / 2; // Round to lower 0.5m
};

/**
 * Calculate final result for Static discipline
 * @param {number[]} timekeeperTimes - Array of times from timekeepers
 * @returns {number} Official time in seconds (rounded down)
 */
export const calculateStaticResult = (timekeeperTimes) => {
  if (!timekeeperTimes || timekeeperTimes.length === 0) return 0;
  const mean = timekeeperTimes.reduce((a, b) => a + b, 0) / timekeeperTimes.length;
  return Math.floor(mean); // Round down to nearest second
};

/**
 * Calculate final result for Depth disciplines
 * @param {number} declaredDepth - Announced depth
 * @param {number} reachedDepth - Actual depth reached
 * @param {boolean} tagRecovered - Whether depth marker was recovered
 * @param {number} additionalPenalties - Any other penalties
 * @returns {object} Result with breakdown
 */
export const calculateDepthResult = (declaredDepth, reachedDepth, tagRecovered = true, additionalPenalties = 0) => {
  const distancePenalty = declaredDepth > reachedDepth ? declaredDepth - reachedDepth : 0;
  const tagPenalty = tagRecovered ? 0 : 1;
  const totalPenalty = distancePenalty + tagPenalty + additionalPenalties;
  const finalResult = reachedDepth - totalPenalty;
  
  return {
    declaredDepth,
    reachedDepth,
    distancePenalty,
    tagPenalty,
    additionalPenalties,
    totalPenalty,
    finalResult: Math.max(0, finalResult)
  };
};

/**
 * Format time for display (seconds to MM:SS or HH:MM:SS)
 * @param {number} totalSeconds - Time in seconds
 * @returns {string} Formatted time string
 */
export const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Get discipline info by code
 * @param {string} code - Discipline code (e.g., 'DYN', 'CWT')
 * @returns {object|null} Discipline information
 */
export const getDisciplineInfo = (code) => {
  const poolDiscipline = CMAS_RULES.disciplines.pool[code];
  if (poolDiscipline) return { ...poolDiscipline, category: 'pool' };
  
  const depthDiscipline = CMAS_RULES.disciplines.depth[code];
  if (depthDiscipline) return { ...depthDiscipline, category: 'depth' };
  
  return null;
};

/**
 * Check if performance is valid based on card
 * @param {string} card - Card code (WHITE, YELLOW, RED)
 * @returns {boolean} Whether performance is valid
 */
export const isPerformanceValid = (card) => {
  return card === 'WHITE' || card === 'YELLOW';
};

/**
 * Check if performance qualifies for record
 * @param {string} card - Card code
 * @returns {boolean} Whether record is valid
 */
export const isRecordValid = (card) => {
  return card === 'WHITE';
};

/**
 * Get penalty details by code
 * @param {string} code - Penalty code
 * @param {string} disciplineType - 'pool' or 'depth'
 * @returns {object|null} Penalty details
 */
export const getPenaltyInfo = (code, disciplineType = 'pool') => {
  const penalties = CMAS_RULES.penalties[disciplineType];
  if (penalties) {
    const found = Object.values(penalties).find(p => p.code === code);
    if (found) return found;
  }
  
  // Check common penalties
  const commonPenalty = Object.values(CMAS_RULES.penalties.common).find(p => p.code === code);
  return commonPenalty || null;
};

export default CMAS_RULES;
